<template>
  <div class="pp">
    <div class="pp-toggle" role="radiogroup" aria-label="Billing period">
      <button type="button" role="radio" :aria-checked="String(!yearly)" :class="{ on: !yearly }" @click="yearly = false">Monthly</button>
      <button type="button" role="radio" :aria-checked="String(yearly)" :class="{ on: yearly }" @click="yearly = true">
        Yearly <span class="pp-save">2 months free</span>
      </button>
    </div>

    <div class="pp-grid">
      <article v-for="p in plans" :key="p.key" class="pp-plan" :class="{ 'pp-featured': p.featured }">
        <div v-if="p.featured" class="pp-ribbon">Most towns choose this</div>
        <h3 class="pp-name">{{ p.name }}</h3>
        <p class="pp-who">{{ p.who }}</p>

        <div class="pp-price">
          <span v-if="p.from" class="pp-from">from</span>
          <span class="pp-amount">${{ (yearly ? p.yearly : p.monthly).toLocaleString() }}</span>
          <span class="pp-per">{{ yearly ? '/ year' : '/ month' }}</span>
        </div>
        <p class="pp-billing">
          <template v-if="yearly">Billed once a year. Setup fee waived.</template>
          <template v-else>Billed monthly. ${{ p.setup.toLocaleString() }} one-time setup.</template>
        </p>

        <div class="pp-emails">
          <v-icon size="18" color="#8A6A35">mdi-email-fast-outline</v-icon>
          <span><strong>{{ p.emails.toLocaleString() }}</strong> newsletter emails a month</span>
        </div>

        <ul class="na-checks pp-list">
          <li v-for="f in p.features" :key="f">{{ f }}</li>
        </ul>

        <router-link to="/demo" class="na-btn pp-cta" :class="p.featured ? 'na-btn-primary' : 'na-btn-outline'">
          {{ p.cta }}<v-icon size="19">mdi-arrow-right</v-icon>
        </router-link>
      </article>
    </div>

    <div class="pp-notes">
      <div class="pp-note">
        <v-icon size="22" color="#14315E">mdi-gift-outline</v-icon>
        <p><strong>30-day free trial</strong> on every plan, with up to 2,000 emails. No credit card.</p>
      </div>
      <div class="pp-note">
        <v-icon size="22" color="#14315E">mdi-scale-balance</v-icon>
        <p><strong>Over your email allowance?</strong> Essential emails keep sending at $2 per 1,000; scheduled newsletters wait for the 1st.</p>
      </div>
      <div class="pp-note">
        <v-icon size="22" color="#14315E">mdi-check-decagram-outline</v-icon>
        <p><strong>Every feature on every plan.</strong> Plans differ only by town size and email allowance.</p>
      </div>
    </div>

    <div v-if="faq" class="pp-faq">
      <h3 class="na-h3 pp-faq-title">Pricing questions</h3>
      <details v-for="q in questions" :key="q.q" class="pp-q">
        <summary>{{ q.q }}<v-icon size="22">mdi-plus</v-icon></summary>
        <p>{{ q.a }}</p>
      </details>
    </div>
  </div>
</template>

<script>
// One source of truth for NewsAppify plans on the marketing site.
// The email allowances match the platform's limits (functions/src/quota.ts).
export default {
  name: 'PricingPlans',
  props: { faq: { type: Boolean, default: false } },
  data: () => ({
    yearly: false,
    plans: [
      {
        key: 'community', name: 'Community', who: 'Towns under 10,000 residents',
        monthly: 399, yearly: 3990, setup: 1500, emails: 25000, cta: 'Start free trial',
        features: ['Branded iPhone and Android apps', 'Website for every section', 'Breaking-news alerts to every phone', 'All nine newsletters', 'Events, RSVPs and reminders', 'Sell ads on your site, apps and newsletters, with advertiser reports', 'Admin, staff and contributor accounts'],
      },
      {
        key: 'town', name: 'Town', who: '10,000 to 50,000 residents', featured: true,
        monthly: 799, yearly: 7990, setup: 2000, emails: 100000, cta: 'Start free trial',
        features: ['Everything in Community', 'Four times the email allowance', 'Priority support by phone and email'],
      },
      {
        key: 'city', name: 'City', who: '50,000+ residents or several publications', from: true,
        monthly: 1499, yearly: 14990, setup: 2500, emails: 300000, cta: 'Talk to us',
        features: ['Everything in Town', 'Custom email allowance', 'Several publications on one contract', 'Custom contract and invoicing'],
      },
    ],
    questions: [
      { q: 'What counts toward the email allowance?', a: 'Every email the platform sends: newsletters, personal digests, breaking-news emails, event reminders, sign-up confirmations and staff alerts. Push alerts to the apps are unlimited and never count.' },
      { q: 'What happens when we reach the limit?', a: 'Your admins get a heads-up at 80%. At 100%, scheduled newsletters and digests pause until the 1st of the month. Breaking news, event reminders, confirmations and urgent-tip alerts keep sending and are billed at $2 per 1,000. You can move up a plan at any time.' },
      { q: 'Is there a setup fee?', a: 'A one-time fee covers designing your apps, the app store listings and setting up your newsroom: $1,500 for Community, $2,000 for Town and $2,500 for City. It\'s waived when you pay yearly.' },
      { q: 'Do we need our own Apple and Google accounts?', a: 'Yes. Apple requires your app to be published from your own developer account ($99 a year, and Apple offers fee waivers for government entities); Google Play is a one-time $25. We set both up with you and handle every submission.' },
      { q: 'Can we cancel?', a: 'Monthly plans cancel anytime with no penalty. Yearly plans run for the year you paid for. Your content and subscriber list are yours to export either way.' },
    ],
  }),
}
</script>

<style scoped>
.pp-toggle {
  display: inline-flex;
  padding: 5px;
  border-radius: 999px;
  background: var(--na-card);
  border: 1px solid var(--na-rule);
  margin-bottom: 40px;
}
.pp-toggle button {
  border: 0;
  background: transparent;
  padding: 11px 22px;
  border-radius: 999px;
  font-family: var(--na-sans);
  font-weight: 600;
  font-size: 0.98rem;
  color: var(--na-ink-2);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.pp-toggle button.on { background: var(--na-navy); color: #fff; }
.pp-save { font-size: 0.75rem; font-weight: 700; padding: 3px 8px; border-radius: 999px; background: #EFE3C9; color: var(--na-brass-text); }
.pp-toggle button.on .pp-save { background: var(--na-brass); color: #14100A; }

.pp-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; align-items: stretch; }
.pp-plan {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 36px 32px 32px;
  background: var(--na-card);
  border: 1px solid var(--na-rule);
  border-radius: 12px;
  box-shadow: 0 28px 50px -40px rgba(16, 27, 45, 0.45);
}
.pp-featured {
  border: 2px solid var(--na-navy);
  box-shadow: 0 34px 70px -38px rgba(20, 49, 94, 0.6);
  transform: translateY(-10px);
}
.pp-ribbon {
  position: absolute;
  top: -15px;
  left: 32px;
  padding: 6px 14px;
  border-radius: 4px;
  background: var(--na-navy);
  color: #fff;
  font-size: 0.8rem;
  font-weight: 700;
}
.pp-name { font-family: var(--na-serif); font-weight: 700; font-size: 1.75rem; margin: 0; color: var(--na-ink); }
.pp-who { margin: 4px 0 24px; font-size: 0.97rem; color: var(--na-ink-2); }
.pp-price { display: flex; align-items: baseline; gap: 6px; flex-wrap: wrap; }
.pp-from { font-family: var(--na-serif); font-style: italic; font-size: 1.1rem; color: var(--na-ink-2); }
.pp-amount { font-family: var(--na-serif); font-weight: 700; font-size: 3.4rem; line-height: 1; letter-spacing: -0.03em; color: var(--na-navy); }
.pp-per { font-size: 1rem; color: var(--na-ink-2); }
.pp-billing { margin: 10px 0 20px; font-size: 0.9rem; color: var(--na-ink-2); min-height: 2.6em; }
.pp-emails {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 8px;
  background: #F6EEDD;
  font-size: 0.95rem;
  color: var(--na-ink);
  margin-bottom: 22px;
}
.pp-list { flex: 1; margin-bottom: 26px; }
.pp-cta { width: 100%; }

.pp-notes { display: grid; grid-template-columns: repeat(3, 1fr); gap: 28px; margin-top: 44px; padding-top: 32px; border-top: 1px solid var(--na-rule); }
.pp-note { display: flex; gap: 14px; align-items: flex-start; }
.pp-note p { margin: 0; font-size: 0.97rem; line-height: 1.55; color: var(--na-ink-2); }
.pp-note strong { color: var(--na-ink); }

.pp-faq { margin-top: 64px; max-width: 820px; }
.pp-faq-title { margin-bottom: 14px; }
.pp-q { border-bottom: 1px solid var(--na-rule); }
.pp-q summary {
  list-style: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 20px 0;
  cursor: pointer;
  font-family: var(--na-serif);
  font-weight: 600;
  font-size: 1.2rem;
  color: var(--na-ink);
}
.pp-q summary::-webkit-details-marker { display: none; }
.pp-q summary .v-icon { color: var(--na-brass-text) !important; transition: transform 0.2s ease; flex-shrink: 0; }
.pp-q[open] summary .v-icon { transform: rotate(45deg); }
.pp-q p { margin: 0 0 22px; font-family: var(--na-serif); font-size: 1.06rem; line-height: 1.7; color: var(--na-ink-2); max-width: 40em; }

@media (max-width: 1000px) {
  .pp-grid { grid-template-columns: 1fr; max-width: 520px; }
  .pp-featured { transform: none; }
  .pp-notes { grid-template-columns: 1fr; }
}
</style>